import React, { useEffect, useRef } from "react";

interface Star {
  x: number;
  y: number;
  radius: number;
  phase: number;
  speed: number;
}

const MAX_STARS = 220;
const PIXELS_PER_STAR = 7000;

/** Twinkling background stars. Static when the visitor prefers reduced motion. */
const StarField: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let stars: Star[] = [];
    let frame = 0;

    const seed = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const count = Math.min(
        MAX_STARS,
        Math.floor((canvas.width * canvas.height) / PIXELS_PER_STAR),
      );
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() + 0.2,
        phase: Math.random() * 6,
        speed: Math.random() * 0.02 + 0.005,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#fff";
      stars.forEach((star) => {
        star.phase += star.speed;
        ctx.globalAlpha = 0.4 + Math.sin(star.phase) * 0.25;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const tick = () => {
      draw();
      frame = requestAnimationFrame(tick);
    };

    const handleResize = () => {
      seed();
      draw();
    };

    seed();
    draw();
    if (!reduceMotion) frame = requestAnimationFrame(tick);
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="al-stars" aria-hidden="true" />;
};

export default StarField;
